import { useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBrain, faPen, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import ReactMarkdown from 'react-markdown';
import { extractBudgetError, type UserMemoryBudgetError } from '../../api';
import {
  useDeleteUserMemory,
  useSetUserMemoryEnabled,
  useUpdateUserMemory,
  useUserMemory,
} from '../../hooks/useUserMemory';
import { remarkPlugins, rehypePlugins } from '../../utils/markdown-config';
import { useNotifications } from '../../../../_lib/notifications/useNotifications';
import { formatDateForLocale } from '../../../../utils/hooks/useDateFormat';
import { studioPortalProps } from '../studioPortal';
import styles from './UserMemorySection.module.css';

const EMPTY_STATE_TEXT =
  'Nothing remembered yet — the assistant learns your preferences as you chat.';

// Used only until the server budget is known (from GET or a 422).
const DEFAULT_TOKEN_BUDGET = 1000;
// Rough client estimate: ~4 characters per token. Deliberately approximate —
// the server 422 stays the authority; this only warns the user early.
const CHARS_PER_TOKEN = 4;
const NEAR_BUDGET_RATIO = 0.9;

function estimateTokens(text: string): number {
  return Math.ceil(text.length / CHARS_PER_TOKEN);
}

function tokenIndicatorColor(overBudget: boolean, nearBudget: boolean): string {
  if (overBudget) {
    return 'red';
  }
  if (nearBudget) {
    return 'yellow';
  }
  return 'dimmed';
}

function formatUpdatedAt(updatedAt: string): string {
  const parsed = new Date(updatedAt);
  return Number.isNaN(parsed.getTime()) ? '' : formatDateForLocale(parsed);
}

export default function UserMemorySection() {
  const memoryQuery = useUserMemory();
  const memory = memoryQuery.data;
  const updateMemory = useUpdateUserMemory();
  const setEnabled = useSetUserMemoryEnabled();
  const deleteMemory = useDeleteUserMemory();
  const notifications = useNotifications();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [budgetError, setBudgetError] = useState<UserMemoryBudgetError | null>(
    null
  );
  const [resetOpen, setResetOpen] = useState(false);

  const enabled = memory?.enabled ?? true;
  const profileMd = memory?.profileMd ?? '';
  const isEmpty = !memory || memory.version === 0 || profileMd.trim() === '';
  const updatedLabel = memory?.updatedAt
    ? formatUpdatedAt(memory.updatedAt)
    : '';

  // Live budget feedback while editing. Prefer the server budget (GET, then a
  // prior 422), else the default. Estimate is heuristic — Save is only blocked
  // client-side; the server 422 remains the authority.
  const tokenBudget =
    memory?.tokenBudget ?? budgetError?.tokenBudget ?? DEFAULT_TOKEN_BUDGET;
  const estimatedTokens = estimateTokens(draft);
  const overBudget = estimatedTokens > tokenBudget;
  const nearBudget =
    !overBudget && estimatedTokens >= tokenBudget * NEAR_BUDGET_RATIO;

  const handleStartEdit = () => {
    setDraft(profileMd);
    setBudgetError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setBudgetError(null);
    setDraft('');
  };

  const handleToggleEnabled = () => {
    const next = !enabled;
    // Disabling greys out (pointer-events: none) the panel that holds the
    // editor — leave edit mode first so the user can't get stranded in it.
    if (!next && isEditing) {
      handleCancelEdit();
    }
    setEnabled.mutate(next, {
      onError: () =>
        notifications.error(
          'Could not update memory',
          'Please try again in a moment.'
        ),
    });
  };

  const handleSave = () => {
    updateMemory.mutate(draft, {
      onSuccess: () => {
        setIsEditing(false);
        setBudgetError(null);
        setDraft('');
        notifications.success('Memory updated');
      },
      onError: (err) => {
        const budget = extractBudgetError(err);
        if (budget) {
          // Over budget: surface `detail` inline and keep the draft intact.
          setBudgetError(budget);
          return;
        }
        notifications.error(
          'Could not save memory',
          'Something went wrong. Please try again.'
        );
      },
    });
  };

  const handleReset = () => {
    deleteMemory.mutate(undefined, {
      onSuccess: () => {
        setResetOpen(false);
        setIsEditing(false);
        setBudgetError(null);
        notifications.success('Memory cleared');
      },
      onError: () =>
        notifications.error('Could not reset memory', 'Please try again.'),
    });
  };

  function renderBody() {
    if (memoryQuery.isLoading) {
      return <Loader size="sm" />;
    }
    if (memoryQuery.isError) {
      return (
        <Text size="sm" c="red">
          Couldn&apos;t load your memory. Please refresh and try again.
        </Text>
      );
    }
    if (isEditing) {
      return renderEditor();
    }
    if (isEmpty) {
      return (
        <Text size="sm" c="dimmed">
          {EMPTY_STATE_TEXT}
        </Text>
      );
    }
    return (
      <div className={styles.markdown} data-testid="memory-markdown">
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
        >
          {profileMd}
        </ReactMarkdown>
      </div>
    );
  }

  function renderEditor() {
    return (
      <Stack gap="sm">
        <Textarea
          value={draft}
          onChange={(event) => {
            setDraft(event.currentTarget.value);
            // Stale over-budget warning shouldn't linger once the user edits.
            if (budgetError) {
              setBudgetError(null);
            }
          }}
          autosize
          minRows={10}
          maxRows={24}
          classNames={{ input: styles.editorInput }}
          aria-label="Edit what the assistant remembers about you"
          data-testid="memory-editor"
        />
        <Text
          size="xs"
          c={tokenIndicatorColor(overBudget, nearBudget)}
          className={styles.tokenIndicator}
          data-testid="token-estimate"
        >
          ~{estimatedTokens.toLocaleString()} / {tokenBudget.toLocaleString()}{' '}
          tokens
        </Text>
        {budgetError && (
          <Alert color="red" variant="light" data-testid="memory-budget-error">
            <Text size="sm">{budgetError.detail}</Text>
            <Text size="xs" c="dimmed" className={styles.tokenIndicator}>
              {budgetError.tokenCount.toLocaleString()} /{' '}
              {budgetError.tokenBudget.toLocaleString()} tokens
            </Text>
          </Alert>
        )}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            onClick={handleCancelEdit}
            disabled={updateMemory.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            loading={updateMemory.isLoading}
            disabled={overBudget}
          >
            Save
          </Button>
        </Group>
      </Stack>
    );
  }

  const bodyClassName = `${styles.body} ${enabled ? '' : styles.bodyDisabled}`;

  return (
    <Stack gap="md" data-testid="user-memory-section">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <FontAwesomeIcon icon={faBrain} />
          <div>
            <Text fw={600}>What the assistant remembers about you</Text>
            {updatedLabel && (
              <Text size="xs" c="dimmed">
                Updated {updatedLabel}
              </Text>
            )}
          </div>
        </Group>
        <Switch
          checked={enabled}
          onChange={handleToggleEnabled}
          disabled={setEnabled.isLoading || memoryQuery.isLoading}
          aria-label="Enable assistant memory"
        />
      </Group>

      <div className={bodyClassName} aria-disabled={!enabled}>
        {renderBody()}
      </div>

      {!isEditing && !memoryQuery.isLoading && !memoryQuery.isError && (
        <Group justify="space-between">
          <Button
            variant="light"
            onClick={handleStartEdit}
            disabled={!enabled}
            leftSection={<FontAwesomeIcon icon={faPen} />}
          >
            Edit
          </Button>
          {!isEmpty && (
            <Button
              variant="subtle"
              color="red"
              onClick={() => setResetOpen(true)}
              leftSection={<FontAwesomeIcon icon={faTrashCan} />}
            >
              Reset memory
            </Button>
          )}
        </Group>
      )}

      <Modal
        opened={resetOpen}
        onClose={() => setResetOpen(false)}
        title={<Text fw={600}>Reset memory</Text>}
        size="sm"
        // #moo-studio-portal only exists under MooStudioLayout. This section
        // now also renders inside the composer's settings modal, where that
        // target is absent — and a portal aimed at a missing node never
        // mounts, so Reset appeared to do nothing at all.
        {...studioPortalProps()}
        // 1500, not 1400: the settings modal this can open inside is itself at
        // 1400, and at equal z-index the confirm can land under its overlay —
        // reproducing the very "Reset does nothing" failure above. Matches the
        // AppsPane disconnect and ToneEditor confirms.
        zIndex={1500}
        styles={{ inner: { left: 0, right: 0 } }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This permanently clears everything the assistant remembers about
            you.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setResetOpen(false)}
              disabled={deleteMemory.isLoading}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleReset}
              loading={deleteMemory.isLoading}
            >
              Reset memory
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
